import { espnService } from './espnService';
import { databaseService } from './databaseService';
import { oddsService } from './oddsService';
import { CURRENT_SEASON } from '../config/season';

export interface LoadProgress {
  currentWeek: number;
  totalWeeks: number;
  gamesLoaded: number;
  isComplete: boolean;
  errors: string[];
}

class DataLoaderService {
  async loadAllSeasonGames(season: number = CURRENT_SEASON, onProgress?: (progress: LoadProgress) => void): Promise<LoadProgress> {
    const progress: LoadProgress = {
      currentWeek: 0,
      totalWeeks: 18,
      gamesLoaded: 0,
      isComplete: false,
      errors: []
    };

    console.log(`Starting to load all games for ${season} NFL season...`);
    
    try {
      // Clear existing games for this season first
      await databaseService.clearSeasonGames(season);
      
      // Load all games from ESPN
      const allGames = await espnService.getAllSeasonGames(season);
      
      if (allGames.length === 0) {
        progress.errors.push(`No games found for ${season} season`);
        return progress;
      }

      // Save to database
      const saveResult = await databaseService.saveGames(allGames);
      
      progress.gamesLoaded = saveResult.success;
      progress.isComplete = true;
      progress.currentWeek = 18;
      
      if (saveResult.errors > 0) {
        progress.errors.push(`${saveResult.errors} games failed to save`);
      }

      console.log(`Season loading complete: ${progress.gamesLoaded} games loaded`);
      
    } catch (error) {
      console.error('Error loading season games:', error);
      progress.errors.push(`Failed to load season: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    if (onProgress) {
      onProgress(progress);
    }

    return progress;
  }

  async refreshWeekGames(season: number, week: number): Promise<{ success: boolean; gamesUpdated: number; error?: string }> {
    try {
      console.log(`Refreshing games for Season ${season}, Week ${week}...`);
      
      const games = await espnService.getWeekGames(week, season);
      const saveResult = await databaseService.saveGames(games);
      
      return {
        success: saveResult.errors === 0,
        gamesUpdated: saveResult.success,
        error: saveResult.errors > 0 ? `${saveResult.errors} games failed to update` : undefined
      };
    } catch (error) {
      console.error(`Error refreshing week ${week}:`, error);
      return {
        success: false,
        gamesUpdated: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async loadLiveOdds(season: number, week?: number): Promise<{ success: number; errors: number }> {
    console.log(`Loading live odds for season ${season}${week ? `, week ${week}` : ''}...`);
    
    try {
      // Get games that need odds updates
      const games = await databaseService.getGames(season, week);
      
      if (games.length === 0) {
        console.log('No games found to update odds for');
        return { success: 0, errors: 0 };
      }

      // Fetch live odds for all games
      const gameIds = games.map(game => ({
        game_id: game.game_id,
        competition_id: game.competition_id
      }));

      const liveOdds = await oddsService.getBatchLiveOdds(gameIds);
      
      // Prepare spread updates
      const spreadUpdates = liveOdds
        .filter(odds => odds.spread)
        .map(odds => ({
          gameId: odds.gameId,
          homeSpread: odds.spread!.home.line
        }));

      if (spreadUpdates.length === 0) {
        console.log('No spread data found in live odds');
        return { success: 0, errors: 0 };
      }

      // Update spreads in database
      const result = await databaseService.batchUpdateSpreads(spreadUpdates);
      
      console.log(`Live odds loading complete: ${result.success} games updated, ${result.errors} errors`);
      return result;

    } catch (error) {
      console.error('Error loading live odds:', error);
      return { success: 0, errors: 1 };
    }
  }

  async getLoadedGames(season: number, week?: number) {
    return await databaseService.getGames(season, week);
  }
}

export const dataLoaderService = new DataLoaderService();